import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import PhoneNumberInput from './input/PhoneNumberInput';
import { getCountry } from '../services/flag';
import { getCountryWithPhoneCode } from '../services/country';
import useGeoLocation from "react-ipgeolocation";
import { registerUser } from '../services/externalAuthenticationService';
import { REGEX } from '../constants/regex';
import { getValidateId, getValidateEmail } from '../services/userService';
import LoadingButton from './common/LoadingButton';
import { ErrorTemplate } from './common/ToastMessage/Template';
import { Toast } from "primereact/toast";
import { translate } from '../localization/translateService';
import moment from "moment-timezone";
import { checkReferralCouponAvailable } from '../services/couponService';

// vuesax/linear eye + eye-slash icons (Figma) for the password show/hide toggle.
const EyeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42 13.98 8.42 12C8.42 10.02 10.02 8.42 12 8.42C13.98 8.42 15.58 10.02 15.58 12Z" stroke="#747F8F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.4C18.82 5.8 15.53 3.72 12 3.72C8.47 3.72 5.18 5.8 2.89 9.4C1.99 10.81 1.99 13.18 2.89 14.59C5.18 18.19 8.47 20.27 12 20.27Z" stroke="#747F8F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const EyeSlashIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.53 9.47L9.47 14.53C8.82 13.88 8.42 12.99 8.42 12C8.42 10.02 10.02 8.42 12 8.42C12.99 8.42 13.88 8.82 14.53 9.47Z" stroke="#747F8F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17.82 5.77C16.07 4.45 14.07 3.73 12 3.73C8.47 3.73 5.18 5.81 2.89 9.41C1.99 10.82 1.99 13.19 2.89 14.6C3.68 15.84 4.6 16.91 5.6 17.77" stroke="#747F8F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.42 19.53C9.56 20.01 10.77 20.27 12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.4C20.78 8.88 20.42 8.39 20.05 7.93" stroke="#747F8F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.51 12.7C15.25 14.11 14.1 15.26 12.69 15.52" stroke="#747F8F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.47 14.53L2 22" stroke="#747F8F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 2L14.53 9.47" stroke="#747F8F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// vuesax/linear chevron for the "Have a referral code?" expandable link (Figma 81:2815).
const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// vuesax/linear calendar icon (Figma 2330-5470) for the Date of Birth trigger,
// replacing PrimeReact's default pi-calendar glyph. Full 6-dot grid per design
// (the previous version was missing 3 dots, so it read as a different icon).
const CalendarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V5" stroke="#747F8F" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 2V5" stroke="#747F8F" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.5 9.09H20.5" stroke="#747F8F" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="#747F8F" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.6947 13.7H15.7037" stroke="#747F8F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.6947 16.7H15.7037" stroke="#747F8F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11.9955 13.7H12.0045" stroke="#747F8F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11.9955 16.7H12.0045" stroke="#747F8F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.29431 13.7H8.30329" stroke="#747F8F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.29431 16.7H8.30329" stroke="#747F8F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Per-language fallbacks for the Student / Tutor tabs (sheet keys pending).
const TAB_LABEL_FALLBACKS = {
  en: { student: "Student", teacher: "Tutor" },
  ko: { student: "학생", teacher: "강사" },
  vi: { student: "Học viên", teacher: "Gia sư" },
};

// Per-language fallbacks for the referral-code toggle (sheet 2 key pending).
const REFERRAL_TOGGLE_FALLBACKS = {
  en: "Have a referral code?",
  ko: "추천 코드가 있으신가요?",
  vi: "Bạn có mã giới thiệu?",
};

// Email-divider label (Figma 2297: "OR CONTINUE WITH EMAIL"). Mirrors the Login
// theme's `orContinueWithEmail`. The legacy CMS value "SIGN UP WITH" is treated as
// unset so the design wording shows everywhere without a backoffice edit; a NEW
// CMS value still overrides.
const OR_CONTINUE_WITH_EMAIL = {
  en: "OR CONTINUE WITH EMAIL",
  ko: "또는 이메일로 계속하기",
  vi: "HOẶC TIẾP TỤC VỚI EMAIL",
};

const Register = ({
  props
}) => {

  const {
    control,
    trigger,
    setValue,
    watch,
    formState: { errors: formErrors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
    language: props.lang,
    id: '',
    password:'',
    confirmPassword: '',
    nationality: '',
    firstName: '',
    lastName: '',
    birthday: '',
    gender:'male',
    phoneNumberCode: '',
    phoneNumber: '',
    email: '',
    role: 'Student',
    termAgree: false,
    timeZone: ''
    }
  });

  const registerTutorReturnUrl = props.returnUrl || `/${props.lang}/tutor-onboarding?isOpenForm=true`;
  const registerStudentReturnUrl = props.returnUrl || `/${props.lang}`;
  const location = useGeoLocation();
  const [isReferralCouponAvailable, setIsReferralCouponAvailable] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDuplicateId, setIsDuplicateId] = useState(false);
  const [isDuplicateEmail, setIsDuplicateEmail] = useState(false);
  const [emailExistsMsg, setEmailExistsMsg] = useState(props.emailInputInvalidLabel || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [countryCode, setCountryCode] = useState(null);
  const [passwordValidation, setPasswordValidation] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasSpecialChar: false,
    hasDigit: false,
    isMatch: false,
    showValidation: false
  });

  const [countries, setCountries] = useState([]);
  const [phoneCountries, setPhoneCountries] = useState([]);
  const usernameValidationTimeoutRef = useRef(null);
  const emailValidationTimeoutRef = useRef(null);
  const [isOpenForm, setIsOpenForm] = useState(!!props.isTutor);
  const [invitationCode, setInvitationCode] = useState('');
  // Section labels — translated via existing keys (already referenced by RegisterTabs),
  // with English fallbacks. No new i18n keys are introduced.
  const [tabLabels, setTabLabels] = useState({ student: 'Student', teacher: 'Tutor' });
  const [referralLabel, setReferralLabel] = useState(
    REFERRAL_TOGGLE_FALLBACKS[String(props.lang || 'en').slice(0, 2).toLowerCase()] || REFERRAL_TOGGLE_FALLBACKS.en
  );
  const VALIDATION_DELAY = 500;
  const emailRegex = REGEX.EMAIL;
  const idRegex = REGEX.ID;

  const errorToast = useRef(null);
  useEffect(() => {
    const loadCountryData = async () => {
      try {

        const [
          countryData,
          phoneOptions,
        ] = await Promise.all([
          getCountry(),
          getCountryWithPhoneCode(location.country),
        ]);

        setCountries(countryData);
        setPhoneCountries(phoneOptions);

      } catch (error) {
        // console.error('Error loading country data:', error);
        errorToast?.current?.show({
        severity: "error",
        summary: "",
        detail: "",
        life: 3000,
        content: (props) => ErrorTemplate(props)
      });
      }
    };

    loadCountryData();
  }, []);

  useEffect(() => {
    const timezone = moment.tz.guess();
    setValue('timeZone', timezone);
  }, []);

  // Tab labels (Student / Tutor). The TAB_STUDENT/TAB_TEACHER keys are not yet in
  // the translation sheet, so translate() would always return the English
  // fallback and the tabs would never localise. Use per-language fallbacks keyed
  // off the page language (the page reloads on a language change, so props.lang
  // is current) — the translate() keys still win once added to the sheet.
  useEffect(() => {
    let alive = true;
    (async () => {
      const lang = String(props.lang || "en").slice(0, 2).toLowerCase();
      const fb = TAB_LABEL_FALLBACKS[lang] || TAB_LABEL_FALLBACKS.en;
      const refFb = REFERRAL_TOGGLE_FALLBACKS[lang] || REFERRAL_TOGGLE_FALLBACKS.en;
      try {
        const [student, teacher, referral] = await Promise.all([
          translate("UMBRACO_LANDING.REGISTER.TAB_STUDENT", false, fb.student),
          translate("UMBRACO_LANDING.REGISTER.TAB_TEACHER", false, fb.teacher),
          translate("UMBRACO_LANDING.REGISTER.HAVE_REFERRAL", false, refFb),
        ]);
        if (alive) {
          setTabLabels({ student: student || fb.student, teacher: teacher || fb.teacher });
          setReferralLabel(referral || refFb);
        }
      } catch { if (alive) { setTabLabels(fb); setReferralLabel(refFb); } }
    })();
    return () => { alive = false; };
  }, []);

  const getReturnUrl = () => {
    if (invitationCode) {
      return `/invitation-verification/${invitationCode}`;
    }

    if (isOpenForm) {
      return registerTutorReturnUrl;
    }

    return registerStudentReturnUrl;
  }

  useEffect(() => {
    const handleUrlChange = () => {
      const currentUrl = window.location.search;
      const currentSearchParams = new URLSearchParams(currentUrl);
      const queryString = currentSearchParams.get('returnUrl');

      if (queryString) {
        const params = queryString.split('?');
        const searchParams = new URLSearchParams(params[1]);
        const isOpenFormParam = searchParams.get('isOpenForm');
        setIsOpenForm(isOpenFormParam === 'true');
        return;
      }

      setIsOpenForm(!!props.isTutor);

      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('INVITATION_CODE='));
      if (cookieValue) {
        setInvitationCode(cookieValue.split('=')[1]);
      }
    };

    handleUrlChange();

    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  // Fallback for the duplicate-email message when the CMS label is empty (e.g. EN not filled).
  useEffect(() => {
    if (props.emailInputInvalidLabel) { setEmailExistsMsg(props.emailInputInvalidLabel); return; }
    let alive = true;
    translate("UMBRACO_LANDING.REGISTER.EMAIL_EXISTS", false, "Email already exists.")
      .then((m) => { if (alive) setEmailExistsMsg(m || "Email already exists."); })
      .catch(() => { if (alive) setEmailExistsMsg("Email already exists."); });
    return () => { alive = false; };
  }, [props.emailInputInvalidLabel]);

  useEffect(() => {
    const checkReferralCoupon = async () => {
      try {
        const isAvailable = await checkReferralCouponAvailable();
        setIsReferralCouponAvailable(isAvailable);
      } catch {
        setIsReferralCouponAvailable(false);
      }
    };

    checkReferralCoupon();
  }, []);

  useEffect(() => {
    return () => {
      if (usernameValidationTimeoutRef.current) {
        clearTimeout(usernameValidationTimeoutRef.current);
      }
      if (emailValidationTimeoutRef.current) {
        clearTimeout(emailValidationTimeoutRef.current);
      }
    };


  }, []);

  const hasSpecialChars = (str) => {
    const specialChars = /(?=.*\W)/;
    return specialChars.test(str);
  };

  const isValidID = (value) => {
    return idRegex.test(value);
  };

  const preventNumberInput = (event) => {
    const allowedKeys = [8, 9, 27, 13, 46, 37, 38, 39, 40];

    // Allow navigation keys
    if (allowedKeys.includes(event.keyCode)) {
      return;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if ((event.ctrlKey || event.metaKey) &&
        [65, 67, 86, 88, 90].includes(event.keyCode)) {
      return;
    }

    // Prevent number keys (0-9) and numpad (96-105)
    if ((event.keyCode >= 48 && event.keyCode <= 57) ||
        (event.keyCode >= 96 && event.keyCode <= 105)) {
      event.preventDefault();
    }
  };

  const sanitizeNameInput = (value) => {
    if (!value) return '';

    let cleaned = value.replace(/[0-9]/g, '');
    cleaned = cleaned.replace(/^\s+/, '');
    cleaned = cleaned.replace(/\s{2,}/g, ' ');

    return cleaned;
  };

  const handleInputChange = (field, value) => {
    if (field === 'firstName' || field === 'lastName') {
      value = sanitizeNameInput(value);
    }

    setValue(field, value, { shouldValidate: true });

    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }

    if (field === 'password') {
      validatePasswordStrength(value);
    } else if (field === 'confirmPassword') {
      validatePasswordMatch(value, control.password);
    } else if (field === 'id') {
      handleUsernameValidation(value);
    } else if (field === 'email') {
      handleEmailValidation(value);
    }
  };

  const validatePasswordStrength = (password) => {
    const hasMinLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasSpecialChar = hasSpecialChars(password);
    const hasDigit = /\d/.test(password);
    const showValidation = password.length > 0 && !(hasMinLength && hasUpperCase && hasSpecialChar && hasDigit);
    setPasswordValidation(prev => ({
      ...prev,
      hasMinLength,
      hasUpperCase,
      hasSpecialChar,
      hasDigit,
      showValidation: showValidation
    }));

    const currentConfirmPassword = watch('confirmPassword');
    if (currentConfirmPassword) {
      validatePasswordMatch(currentConfirmPassword, password);
    }
  };

  const validatePasswordMatch = (confirmPassword, currentPassword = control.password) => {
    const isMatch = currentPassword && confirmPassword && currentPassword === confirmPassword;

    setPasswordValidation(prev => ({
      ...prev,
      isMatch
    }));

    return isMatch || !confirmPassword || props.passwordInputNotMatch;
  };

  const validateUsernameAPI = async (username) => {
    try {
      const response = await getValidateId(username);
      return response;
    } catch (error) {
      return false;
    }
  };

  const validateEmailAPI = async (email) => {
    try {
      const response = await getValidateEmail(email);
      return response;
    } catch (error) {
      return false;
    }
  };

  const handleUsernameValidation = (username) => {
    if (usernameValidationTimeoutRef.current) {
      clearTimeout(usernameValidationTimeoutRef.current);
    }

    if (username && isValidID(username)) {
      usernameValidationTimeoutRef.current = setTimeout(async () => {
        const isValidUsername = await validateUsernameAPI(username);
        setIsDuplicateId(!isValidUsername);
        if (!isValidUsername) {
          setValidationErrors(prev => ({
            ...prev,
            id: props.idInputInvalidLabel
          }));
        } else {
          setValidationErrors(prev => ({
            ...prev,
            id: ''
          }));
        }
      }, VALIDATION_DELAY);
    }
  };

  const handleEmailValidation = (email) => {
    if (emailValidationTimeoutRef.current) {
      clearTimeout(emailValidationTimeoutRef.current);
    }

    if (email && emailRegex.test(email)) {
      emailValidationTimeoutRef.current = setTimeout(async () => {
        const isValidEmail = await validateEmailAPI(email);
        setIsDuplicateEmail(!isValidEmail);
        if (!isValidEmail) {
          setValidationErrors(prev => ({
            ...prev,
            email: emailExistsMsg || props.emailInputInvalidLabel
          }));
        } else {
          setValidationErrors(prev => ({
            ...prev,
            email: ''
          }));
        }
      }, VALIDATION_DELAY);
    }
  };

  const validateUsernameForSubmit = async (username) => {
    if (!username || !isValidID(username)) {
      return false;
    }

    const isValidUsername = await validateUsernameAPI(username);
    setIsDuplicateId(!isValidUsername);
    if (!isValidUsername) {
      setValidationErrors(prev => ({
        ...prev,
        id: props.idInputInvalidLabel
      }));
      return false;
    } else {
      setValidationErrors(prev => ({
        ...prev,
        id: ''
      }));
      return true;
    }
  };

  const validateEmailForSubmit = async (email) => {
    if (!email || !emailRegex.test(email)) {
      return false;
    }

    const isValidEmail = await validateEmailAPI(email);
    setIsDuplicateEmail(!isValidEmail);
    if (!isValidEmail) {
      setValidationErrors(prev => ({
        ...prev,
        email: emailExistsMsg || props.emailInputInvalidLabel
      }));
      return false;
    } else {
      setValidationErrors(prev => ({
        ...prev,
        email: ''
      }));
      return true;
    }
  };

  const scrollToElement = (element) => {
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });

      element.focus();
      return;
    }
  }

  const scrollDialogToInvalidInput = () => {
    const invalidInput = document.querySelector('.p-invalid');

    if (invalidInput) {
      scrollToElement(invalidInput);
      return;
    }

    const invalidTags = document.querySelector('.border-danger');
    if (invalidTags) {
      scrollToElement(invalidTags);
      return;
    }
  };

  // Form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    const formData = watch();

    const isValid = await trigger(['phoneNumber', 'id', 'password', 'confirmPassword', 'nationality', 'firstName', 'lastName', 'birthday', 'email', 'termAgree']);

    if (!isValid) {
      scrollDialogToInvalidInput();
      return;
    }

    const isUsernameValid = await validateUsernameForSubmit(formData.id);
    const isEmailValid = await validateEmailForSubmit(formData.email);

    if (!isUsernameValid || !isEmailValid) {
      scrollDialogToInvalidInput();
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = watch();

      const registrationData = {
        UserName: formData.id,
        Password: formData.password,
        ConfirmPassword: formData.confirmPassword,
        Nationality: formData.nationality,
        FirstName: formData.firstName.trim(),
        LastName: formData.lastName.trim(),
        DateOfBirth: formData.birthdayUtc,
        Gender: formData.gender,
        PhoneNumberCode: formData.phoneNumberCode,
        PhoneNumber: formData.phoneNumber,
        Email: formData.email,
        Language: formData.language,
        TimeZone: formData.timeZone,
        Role: formData.role,
        ReferralCode: showReferral ? formData.referralCode : null
      };

        const response = await registerUser(registrationData, getReturnUrl());

      if (response.status === 200) {
        window.location.href = response.data;
      }

    } catch (error) {
      const errorMessage = await translate(error.response?.data);
      errorToast?.current?.show({
        severity: "error",
        summary: "",
        detail: errorMessage,
        life: 3000,
        content: (props) => ErrorTemplate(props)
      });
    }

    setIsSubmitting(false);
  };

  // Toggle password visibility
  const togglePasswordVisibility = (inputId) => {
    const input = document.getElementById(inputId);
    const icon = document.getElementById(`${inputId}Icon`);

    if (input && icon) {
      if (input.type === 'password') {
        input.type = 'text';
        icon.classList.remove('pi-eye-slash');
        icon.classList.add('pi-eye');
      } else {
        input.type = 'password';
        icon.classList.remove('pi-eye');
        icon.classList.add('pi-eye-slash');
      }
    }
  };

  const ValidationMessage = ({ field, message }) => {
    if (!validationErrors[field] && !message) return null;

    return (
      <small className="keduall-typography text-danger-txt text-14-normal-400 visibility-visible">
        {message || validationErrors[field]}
      </small>
    );
  };

  const PasswordStrengthIndicator = ({ isValid, text }) => {
    const icon = isValid ?
      <i className="flex justify-content-center align-items-center pi pi-check keduall-icon validation-success-icon"></i> :
      <i className="flex justify-content-center align-items-center pi pi-times keduall-icon validation-fail-icon"></i>;

    return (
      <div className="align-items-center flex flex-row gap-0625">
        <p className="flex">{text}</p>
        {icon}
      </div>
    );
  };

  // Email divider: use the CMS value only if it's a real, non-legacy label;
  // otherwise show the design's "OR CONTINUE WITH EMAIL" in the page language.
  const dividerLang = String(props.lang || 'en').slice(0, 2).toLowerCase();
  const cmsSignUpWith = (props.signUpWithLabel || '').trim();
  const signUpWithText =
    (cmsSignUpWith && cmsSignUpWith.toUpperCase() !== 'SIGN UP WITH')
      ? cmsSignUpWith
      : (OR_CONTINUE_WITH_EMAIL[dividerLang] || OR_CONTINUE_WITH_EMAIL.en);

  return (
    <div className="register-v2">
      <Toast ref={errorToast} position="bottom-right" className="keduall-toast" />

      <div className="register-card">
        <h1 className="reg-title">{props.registerHeading}</h1>

        <div className="reg-social">
          <a
            className="reg-social-btn"
            href={`/auth/login?lang=${props.lang}&kc_idp_hint=${props.google}&returnUrl=${getReturnUrl()}`}
          >
            <img src={props.googleImage} className="reg-social-icon" alt="" aria-hidden="true" />
            <span>{props.signUpGoogleLabel || 'Sign up with Google'}</span>
          </a>
          <a
            className="reg-social-btn"
            href={`/auth/login?lang=${props.lang}&kc_idp_hint=${props.facebook}&returnUrl=${getReturnUrl()}`}
          >
            <img src={props.facebookImage} className="reg-social-icon" alt="" aria-hidden="true" />
            <span>{props.signUpFacebookLabel || 'Sign up with Facebook'}</span>
          </a>
        </div>

        {/* Divider */}
        <div className="reg-divider">
          <span>{signUpWithText}</span>
        </div>

        <form onSubmit={handleSubmit} className="reg-form">
          {/* Hidden language field */}
          <input id="registerModel_language" type="hidden" name="registerModel.Language" value={control.language} />

          {/* Username / ID */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_id">{props.idInputLabel}</label>
            <Controller
              name="id"
              control={control}
              rules={{
                required: props.idInputRequiredLabel,
                minLength: { value: 3, message: props.idInputRequiredLabel },
                pattern: { value: idRegex, message: props.idInputRequiredLabel }
              }}
              render={({ field, fieldState }) => (
                <>
                  <input
                    {...field}
                    id="registerModel_id"
                    name="registerModel.UserName"
                    type="text"
                    maxLength="50"
                    placeholder={props.idInputPlaceholder || 'Enter your username'}
                    className={`reg-input ${(fieldState.error?.message || isDuplicateId) ? 'border-danger' : ''}`}
                    onChange={(e) => { field.onChange(e.target.value); handleInputChange('id', e.target.value); }}
                  />
                  <ValidationMessage field="id" message={fieldState.error?.message} />
                </>
              )}
            />
          </div>

          {/* Password */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_password">{props.passwordInputLabel}</label>
            <Controller
              name="password"
              control={control}
              rules={{
                required: props.passwordInputRequiredLabel,
                minLength: { value: 8, message: props.lengthReqText },
                validate: {
                  hasUpperCase: (value) => /[A-Z]/.test(value) || props.upperReqText,
                  hasSpecialChar: (value) => hasSpecialChars(value) || props.specialCharReqText,
                  hasDigit: (value) => /\d/.test(value) || props.digitReqText
                }
              }}
              render={({ field, fieldState }) => (
                <div className="reg-input-group">
                  <input
                    {...field}
                    id="registerModel_password"
                    name="registerModel.Password"
                    type={showPwd ? 'text' : 'password'}
                    maxLength="64"
                    placeholder={props.passwordInputPlaceholder || 'Enter your password'}
                    className={`reg-input has-suffix ${fieldState.error?.message ? 'border-danger' : ''}`}
                    autoComplete="new-password"
                    onChange={(e) => { field.onChange(e.target.value); handleInputChange('password', e.target.value); }}
                  />
                  <span className="reg-input-suffix cursor-pointer" onClick={() => setShowPwd((v) => !v)} role="button" aria-label="toggle password">
                    {showPwd ? <EyeIcon /> : <EyeSlashIcon />}
                  </span>
                  <ValidationMessage field="password" message={fieldState.error?.message} />
                </div>
              )}
            />
          </div>

          {/* Password strength */}
          {passwordValidation.showValidation && (
            <div className="keduall-typography text-danger-txt text-14-normal-400 visibility-visible">
              {props.passwordStrengthValidation}
              <PasswordStrengthIndicator isValid={passwordValidation.hasMinLength} text={props.lengthReqText} />
              <PasswordStrengthIndicator isValid={passwordValidation.hasUpperCase} text={props.upperReqText} />
              <PasswordStrengthIndicator isValid={passwordValidation.hasSpecialChar} text={props.specialReqText} />
              <PasswordStrengthIndicator isValid={passwordValidation.hasDigit} text={props.digitReqText} />
            </div>
          )}

          {/* Confirm Password */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_confirmPassword">{props.confirmPasswordInputLabel}</label>
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: props.confirmPasswordInputRequiredLabel || 'Please confirm your password',
                validate: (value) => validatePasswordMatch(value, control._formValues.password)
              }}
              render={({ field, fieldState }) => (
                <div className="reg-input-group">
                  <input
                    {...field}
                    id="registerModel_confirmPassword"
                    name="registerModel.ConfirmPassword"
                    type={showConfirmPwd ? 'text' : 'password'}
                    maxLength="64"
                    placeholder={props.confirmPasswordInputPlaceholder || 'Re-enter your password'}
                    className={`reg-input has-suffix ${fieldState.error?.message ? 'border-danger' : ''}`}
                    autoComplete="new-password"
                    onChange={(e) => { field.onChange(e.target.value); handleInputChange('confirmPassword', e.target.value); }}
                  />
                  <span className="reg-input-suffix cursor-pointer" onClick={() => setShowConfirmPwd((v) => !v)} role="button" aria-label="toggle password">
                    {showConfirmPwd ? <EyeIcon /> : <EyeSlashIcon />}
                  </span>
                  <ValidationMessage field="confirmPassword" message={fieldState.error?.message} />
                </div>
              )}
            />
          </div>

          {/* Email */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_email">{props.emailInputLabel}</label>
            <Controller
              name="email"
              control={control}
              rules={{
                required: props.emailInputRequiredLabel,
                pattern: { value: emailRegex, message: props.emailInputRequiredLabel }
              }}
              render={({ field, fieldState }) => (
                <>
                  <input
                    {...field}
                    id="registerModel_email"
                    name="registerModel.Email"
                    type="email"
                    maxLength="254"
                    placeholder={props.emailInputPlaceholder || 'example@dotore.com'}
                    className={`reg-input ${(fieldState.error?.message || isDuplicateEmail) ? 'border-danger' : ''}`}
                    autoComplete="username"
                    onChange={(e) => { field.onChange(e.target.value); handleInputChange('email', e.target.value); }}
                  />
                  <ValidationMessage field="email" message={fieldState.error?.message} />
                </>
              )}
            />
          </div>

          {/* PERSONAL INFO section */}
          <div className="reg-section-divider"><span>{props.personalInfoLabel || 'PERSONAL INFO'}</span></div>

          {/* First Name */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_firstName">{props.firstNameInputLabel}</label>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: props.firstNameInputRequiredLabel }}
              render={({ field, fieldState }) => (
                <>
                  <input
                    {...field}
                    id="registerModel_firstName"
                    name="registerModel.FirstName"
                    type="text"
                    maxLength="50"
                    placeholder={props.firstNameInputPlaceholder || 'Enter your first name'}
                    className={`reg-input ${fieldState.error?.message ? 'border-danger' : ''}`}
                    onChange={(e) => { field.onChange(e.target.value); handleInputChange('firstName', e.target.value); }}
                    onKeyDown={preventNumberInput}
                  />
                  <ValidationMessage field="firstName" message={fieldState.error?.message} />
                </>
              )}
            />
          </div>

          {/* Last Name */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_lastName">{props.lastNameInputLabel}</label>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: props.lastNameInputRequiredLabel }}
              render={({ field, fieldState }) => (
                <>
                  <input
                    {...field}
                    id="registerModel_lastName"
                    name="registerModel.LastName"
                    type="text"
                    maxLength="50"
                    placeholder={props.lastNameInputPlaceholder || 'Enter your last name'}
                    className={`reg-input ${fieldState.error?.message ? 'border-danger' : ''}`}
                    onChange={(e) => { field.onChange(e.target.value); handleInputChange('lastName', e.target.value); }}
                    onKeyDown={preventNumberInput}
                  />
                  <ValidationMessage field="lastName" message={fieldState.error?.message} />
                </>
              )}
            />
          </div>

          {/* Date of Birth */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_birthday">{props.birthdayInputLabel}</label>
            <Controller
              name="birthday"
              control={control}
              rules={{
                required: props.birthdayInputRequiredLabel,
                validate: (value) => {
                  if (value) {
                    const selectedDate = new Date(value);
                    const minDate = new Date('1900-01-01');
                    const maxDate = new Date();
                    return (selectedDate >= minDate && selectedDate <= maxDate);
                  }
                  return true;
                }
              }}
              render={({ field, fieldState }) => (
                <>
                  <Calendar
                    id="registerModel_birthday"
                    value={field.value ? moment(field.value).toDate() : null}
                    onChange={(e) => {
                      const date = moment(e.value);
                      const dateLocal = e.value ? date.local().format('YYYY-MM-DD') : '';
                      const dateUtc = e.value ? date.utc().format('YYYY-MM-DD') : '';
                      field.onChange(dateLocal);
                      setValue('birthday', dateLocal);
                      setValue('birthdayUtc', dateUtc);
                    }}
                    showIcon
                    icon={<CalendarIcon />}
                    dateFormat="yy-mm-dd"
                    placeholder={props.birthdayInputPlaceholderLabel || 'yyyy-mm-dd'}
                    minDate={new Date('1900-01-01')}
                    maxDate={new Date()}
                    showButtonBar
                    className="reg-calendar w-full"
                    inputClassName={`reg-input ${fieldState.error?.message ? 'border-danger' : ''}`}
                    panelClassName="p-3 keduall-border-radius r-12 keduall-form form-input-calendar-panel-default-filled-large"
                  />
                  <ValidationMessage field="birthday" message={fieldState.error?.message} />
                </>
              )}
            />
          </div>

          {/* Gender */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_gender">{props.genderInputLabel}</label>
            <div className="reg-radio-row">
              <label htmlFor="male" className="reg-radio">
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="keduall-radio checkbox-base"
                      type="radio"
                      id="male"
                      name="registerModel.Gender"
                      value="male"
                      checked={field.value === 'male'}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {props.maleGenderInputLabel}
              </label>
              <label htmlFor="female" className="reg-radio">
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="keduall-radio checkbox-base"
                      type="radio"
                      id="female"
                      name="registerModel.Gender"
                      value="female"
                      checked={field.value === 'female'}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {props.femaleGenderInputLabel}
              </label>
            </div>
          </div>

          {/* Nationality */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_nationality">{props.nationalityInputLabel}</label>
            <Controller
              name="timeZone"
              control={control}
              render={({ field }) => (
                <input {...field} id="registerModel_timeZone" type="hidden" name="registerModel.TimeZone" />
              )}
            />
            <Controller
              name="nationality"
              control={control}
              rules={{ required: props.nationalityInputRequiredLabel }}
              render={({ field, fieldState }) => (
                <>
                  <Dropdown
                    id="registerModel_nationality"
                    value={field.value}
                    onChange={(e) => field.onChange(e.value)}
                    options={countries}
                    optionLabel={(country) => country.name}
                    optionValue="code"
                    placeholder={props.nationalityInputPlaceholderLabel}
                    filter
                    filterBy="name"
                    appendTo='self'
                    className={`reg-dropdown w-full ${fieldState.error?.message ? 'border-danger' : ''}`}
                    panelClassName="keduall-form dropdown-input-panel"
                    itemTemplate={(country) => (
                      <div className="flex align-items-center">
                        <span className={`fi fi-${country.code.toLowerCase()} mr-2`} />
                        {country.name}
                      </div>
                    )}
                    valueTemplate={(country) => {
                      if (country) {
                        const selectedCountry = countries.find(c => c.code === country.code);
                        if (selectedCountry) {
                          return (
                            <div className="flex align-items-center">
                              <span className={`fi fi-${selectedCountry.code.toLowerCase()} mr-2`} />
                              {selectedCountry.name}
                            </div>
                          );
                        }
                      }
                      return props.nationalityInputPlaceholderLabel;
                    }}
                  />
                  <input type="hidden" name="registerModel.Nationality" value={field.value || ''} />
                  <ValidationMessage field="nationality" message={fieldState.error?.message} />
                </>
              )}
            />
          </div>

          {/* Phone Number */}
          <div className="reg-field">
            <label className="reg-label required" htmlFor="registerModel_phoneNumberCode">{props.phoneNumberInputLabel}</label>
            <Controller
              name="phoneNumber"
              control={control}
              defaultValue={control.phoneNumber || ''}
              rules={{
                pattern: { value: /^[0-9]{4,10}$/, message: props.phoneNumberInputRequiredLabel }
              }}
              render={({ field, fieldState }) => (
                <>
                  <PhoneNumberInput
                    value={field.value || ''}
                    onchange={(data) => {
                      field.onChange(data.value);
                      if (data.data?.dialCode) {
                        setCountryCode(data.data.country);
                        setValue('phoneNumberCode', data.data.dialCode);
                      }
                    }}
                    placeholder={props.phoneNumberInputPlaceholderLabel || '090 1234 5678'}
                    invalidMessage={props.phoneNumberInputRequiredLabel}
                    country={countryCode}
                    options={phoneCountries}
                    hasError={formErrors.phoneNumber}
                  />
                  <input type="hidden" name="registerModel.PhoneNumber" value={field.value || ''} />
                </>
              )}
            />
          </div>

          {/* Referral code — always-visible collapsible link (Figma 81:2815). Expanded =>
             the code is submitted/validated; collapsed => ignored (independent of coupon campaigns). */}
          <div className="reg-referral">
            <button
              type="button"
              className={`reg-referral-toggle ${showReferral ? 'is-open' : ''}`}
              aria-expanded={showReferral}
              onClick={() => setShowReferral(v => !v)}
            >
              <ChevronDownIcon />
              {referralLabel}
            </button>
            {showReferral && (
              <div className="reg-field reg-referral-field">
                <label className="reg-label" htmlFor="registerModel_referralCode">{props.referralCodeInputLabel}</label>
                <Controller
                  name="referralCode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <input
                      {...field}
                      id="registerModel_referralCode"
                      name="registerModel.ReferralCode"
                      type="text"
                      maxLength="254"
                      className={`reg-input ${fieldState.error?.message ? 'border-danger' : ''}`}
                      autoComplete="off"
                      onChange={(e) => { field.onChange(e.target.value); handleInputChange('referralCode', e.target.value); }}
                    />
                  )}
                />
              </div>
            )}
          </div>

          {/* Terms */}
          <div className="reg-terms">
            <Controller
              name="termAgree"
              control={control}
              rules={{ required: 'You must agree to the terms and conditions' }}
              render={({ field: { value, onChange } }) => (
                <input
                  type="checkbox"
                  id="registerModel_termAgree"
                  name="registerModel_termAgree"
                  checked={value || false}
                  onChange={(e) => { onChange(e.target.checked); handleInputChange('termAgree', e.target.checked); }}
                />
              )}
            />
            <label htmlFor="registerModel_termAgree" className="keduall-checkbox checkbox-base">
              <i className="pi pi-check" />
            </label>
            <label className="reg-terms-text select-none">
              {props.agreeTermsLabel}{' '}
              <a href={`/${props.lang}/terms`} target="_blank" rel="noopener noreferrer">
                <span className="reg-link">{props.termsLabel}</span>
              </a>
              {' '}{props.andLabel || 'and'}{' '}
              <a href={`/${props.lang}/privacy-policy`} target="_blank" rel="noopener noreferrer">
                <span className="reg-link">{props.privacyPolicyLabel || 'Privacy Policy'}</span>
              </a>
            </label>
          </div>

          {/* Submit */}
          <LoadingButton
            isLoading={isSubmitting}
            label={props.registerButtonLabel}
            onClick={handleSubmit}
            className="reg-submit keduall-buttons"
          ></LoadingButton>

          {/* Footer login link */}
          <div className="reg-footer">
            <span>{props.haveAccount}</span>
            <a className="reg-link" href={`/auth/login?returnUrl=${props.returnUrl}&lang=${props.lang}`}>
              {props.returnLogin}
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
